import React, { useRef, useState, useEffect } from 'react';
import { Canvas } from '@react-three/fiber';
import { OrbitControls, Loader } from '@react-three/drei';
import EnvironmentWrapper from './components/EnvironmentWrapper';
import SceneBackground from './components/SceneBackground';
import { 
  FaFolderOpen, 
  FaPalette, 
  FaImage, 
  FaDice, 
  FaLightbulb, 
  FaMapMarkerAlt, 
  FaSyncAlt, 
  FaCamera, 
  FaDownload, 
  FaCog,
  FaRocket,
  FaSquare,
  FaUndo,
  FaSignOutAlt
} from 'react-icons/fa';
import { useAppState } from './hooks/useAppState';
import { useGUI } from './hooks/useGUI';
import { useDefaultClassManager } from './ClassManager';
import { CameraSetup } from './CameraSetup';
import { CameraReset } from './CameraReset';
import { CanvasContent } from './CanvasContent';
import { BoundingBoxDebug } from './BoundingBoxDebug';
import ModelMaterial from './ModelMaterial';
import ModelSpawner from './ModelSpawner';
import PlaneSpawner from './PlaneSpawner';
import TextureProvider from './TextureProvider';
import DirectionalLightComponent from './DirectionalLightComponent';
import AmbientLightComponent from './AmbientLightComponent';
import AccordionPanel from './components/AccordionPanel';
import SynioLogo from './components/SynioLogo';
import Step1Upload from './components/steps/Step1Upload';
import Step1Material from './components/steps/Step1Material';
import Step2Canvas from './components/steps/Step2Canvas';
import Step3Spawn from './components/steps/Step3Spawn';
import Step4Lighting from './components/steps/Step4Lighting';
import RandomizerObjectPosition from './components/steps/RandomizerObjectPosition';
import RandomizerObjectRotation from './components/steps/RandomizerObjectRotation';
import RandomizerBackgroundSpawner from './components/steps/RandomizerBackgroundSpawner';
import RandomizerForegroundSpawner from './components/steps/RandomizerForegroundSpawner';
import RandomizerEnvironment from './components/steps/RandomizerEnvironment';
import BackgroundSpawner from './components/BackgroundSpawner';
import ForegroundSpawner from './components/ForegroundSpawner';
import Step6Export from './components/steps/Step6Export';
import { useAuth } from './contexts/AuthContext';
import AuthForm from './components/Auth/AuthForm';
import MobileWarning from './components/MobileWarning';
import LandingPage from './components/LandingPage';

const App = () => {
  const { user, loading, signOut } = useAuth();
  const state = useAppState();
  const classManager = useDefaultClassManager();
  const [isExporting, setIsExporting] = useState(false);
  const [showAdvancedGUI, setShowAdvancedGUI] = useState(false);
  const [zoom, setZoom] = useState(100);
  const [cameraResetTrigger, setCameraResetTrigger] = useState(0);
  const [currentFrame, setCurrentFrame] = useState(0);
  const [availableHDRs, setAvailableHDRs] = useState([]);
  
  // Check for mobile devices - must be before any early returns
  const [isMobile, setIsMobile] = React.useState(false);
  
  React.useEffect(() => {
    const checkDevice = () => {
      const width = window.innerWidth;
      const height = window.innerHeight;
      const isMobileDevice = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
      const isSmallScreen = width < 1024 || height < 600;
      setIsMobile(isMobileDevice || isSmallScreen);
    };

    checkDevice();
    window.addEventListener('resize', checkDevice);
    
    return () => window.removeEventListener('resize', checkDevice);
  }, []);

  // Lade verfügbare HDR-Dateien
  useEffect(() => {
    fetch(`${process.env.PUBLIC_URL}/hdri/index.json`)
      .then(res => res.json())
      .then(data => {
        const hdrs = Array.isArray(data) ? data : [];
        setAvailableHDRs(hdrs);
      })
      .catch(err => {
        console.warn('Could not load HDR index:', err);
        setAvailableHDRs([]);
      });
  }, []);

  const modelFileInputRef = useRef(null);
  const textureFileInputRef = useRef(null);
  const canvasContentRef = useRef(null);
  const modelSpawnerRef = useRef(null);
  const planeSpawnerRef = useRef(null);
  const modelGroupRef = useRef(null);
  const backgroundSpawnerRef = useRef(null);
  const foregroundSpawnerRef = useRef(null);
  const orbitControlsRef = useRef(null);

  // GUI Callbacks
  const callbacks = {
    setExportYOLO: state.setExportYOLO,
    setShowBoundingBoxes: state.setShowBoundingBoxes,
    setAnnotationType: state.setAnnotationType,
    setMaxFrames: state.setMaxFrames,
    setCanvasSize: state.setCanvasSize,
    setBackgroundColor: state.setBackgroundColor,
    setObjectType: state.setObjectType,
    setCamera: state.setCamera,
    setModelSpawn: state.setModelSpawn,
    setLighting: state.setLighting,
    setEnvironmentCustomHDR: state.setEnvironmentCustomHDR,
    setRandomizers: state.setRandomizers,
    setModelUrl: state.setModelUrl,
    setTextureUrl: state.setTextureUrl,
    setMaterial: state.setMaterial,
    setMaterialTextures: state.setMaterialTextures,
    setRandomizerSettings: state.setRandomizerSettings,
    uploadModel: () => modelFileInputRef.current?.click(),
    uploadTexture: () => textureFileInputRef.current?.click(),
    startExport: () => {
      canvasContentRef.current?.startSavingFrames(state.maxFrames);
    },
    classManager
  };

  // Optional: Advanced GUI (dat.GUI)
  useGUI(showAdvancedGUI ? state : null, showAdvancedGUI ? callbacks : null);

  const handleModelUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      const fileName = file.name.toLowerCase();
      const extension = fileName.split('.').pop();
      
      // Validiere Dateityp
      const validExtensions = ['gltf', 'glb', 'fbx', 'obj'];
      if (!validExtensions.includes(extension)) {
        alert(`Ungültiger Dateityp: .${extension}\nUnterstützte Formate: .gltf, .glb, .fbx, .obj`);
        return;
      }
      
    const url = URL.createObjectURL(file);
      callbacks.setModelUrl(url);
    }
  };

  const handleTextureUpload = (e) => {
    const file = e.target.files[0];
    if (file) callbacks.setTextureUrl(URL.createObjectURL(file));
  };

  // Object Spawners für Export
  const objectSpawners = state.objectType === 'model' 
    ? [{
        ref: modelSpawnerRef,
        classId: 0, // ID bleibt konstant
        className: classManager.getClassName(0) || 'object', // Name kann geändert werden
        type: 'model'
      }]
    : [{
        ref: planeSpawnerRef,
        classId: 1, // ID bleibt konstant
        className: classManager.getClassName(1) || 'label', // Name kann geändert werden
        type: 'plane'
      }];

  // Show loading state while checking authentication
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
          <p className="mt-4 text-gray-600">Lädt...</p>
        </div>
      </div>
    );
  }

  // Show landing page if user is not authenticated (always show, even on mobile)
  if (!user) {
    return <LandingPage />;
  }

  // Show mobile warning only for authenticated users on mobile devices
  if (isMobile) {
    return <MobileWarning />;
  }

  const handleSignOut = async () => {
    const { error } = await signOut();
    if (error) {
      console.error('Error signing out:', error);
    }
  };

  return (
    <main className="absolute w-screen h-screen flex flex-col overflow-hidden" style={{ backgroundColor: '#f5f5f7' }}>
      {/* Logo & Logout - Top Left */}
      <div className="absolute top-4 left-4 z-[1000] flex items-center gap-3">
        <SynioLogo />
        <button
          onClick={handleSignOut}
          className="w-8 h-8 rounded-full border border-dark-border bg-white cursor-pointer flex items-center justify-center text-sm transition-all duration-150 shadow-soft text-dark-text-muted hover:bg-dark-hover hover:text-dark-text focus:outline-none focus:ring-1 focus:ring-dark-text focus:ring-offset-1"
          title="Abmelden"
        >
          <FaSignOutAlt className="text-xs" />
        </button>
      </div>

      {/* Zoom Controls & Bounding Box Toggle - Bottom Left */}
      <div className="absolute bottom-4 left-4 z-[1000] flex items-center gap-2">
        <div className="flex items-center gap-2 bg-white border border-dark-border rounded-full px-3 py-2 shadow-soft">
          <button 
            className="w-7 h-7 rounded-full border border-dark-border bg-white text-dark-text-muted cursor-pointer flex items-center justify-center text-sm font-medium transition-all duration-150 hover:bg-dark-hover hover:text-dark-text focus:outline-none focus:ring-1 focus:ring-dark-text focus:ring-offset-1"
            onClick={() => setZoom(Math.max(25, zoom - 25))}
          >
            −
          </button>
          <span className="text-xs text-dark-text-secondary min-w-[45px] text-center font-medium">{zoom}%</span>
          <button 
            className="w-7 h-7 rounded-full border border-dark-border bg-white text-dark-text-muted cursor-pointer flex items-center justify-center text-sm font-medium transition-all duration-150 hover:bg-dark-hover hover:text-dark-text focus:outline-none focus:ring-1 focus:ring-dark-text focus:ring-offset-1"
            onClick={() => setZoom(Math.min(200, zoom + 25))}
          >
            +
          </button>
        </div>
        <button
          className={`w-9 h-9 rounded-full border border-dark-border bg-white cursor-pointer flex items-center justify-center text-sm transition-all duration-150 shadow-soft focus:outline-none focus:ring-1 focus:ring-dark-text focus:ring-offset-1 ${
            state.showBoundingBoxes 
              ? 'text-dark-text border-dark-text' 
              : 'text-dark-text-muted hover:bg-dark-hover hover:text-dark-text'
          }`}
          onClick={() => callbacks.setShowBoundingBoxes(!state.showBoundingBoxes)}
          title="Bounding Boxes anzeigen"
        >
          <FaSquare className="text-xs" />
        </button>
        <button
          className="w-9 h-9 rounded-full border border-dark-border bg-white cursor-pointer flex items-center justify-center text-sm transition-all duration-150 shadow-soft text-dark-text-muted hover:bg-dark-hover hover:text-dark-text focus:outline-none focus:ring-1 focus:ring-dark-text focus:ring-offset-1"
          onClick={() => {
            // Trigger Camera Reset
            setCameraResetTrigger(prev => prev + 1);
            // Reset OrbitControls falls verfügbar
            if (orbitControlsRef.current && typeof orbitControlsRef.current.reset === 'function') {
              orbitControlsRef.current.reset();
            }
          }}
          title="Kamera zurücksetzen"
        >
          <FaCamera className="text-xs" />
        </button>
      </div>

      {/* Main Content Area */}
      <div className="flex-1 flex overflow-hidden h-full">
        {/* Canvas Area */}
        <div className="flex-1 flex items-center justify-center relative canvas-pattern overflow-hidden" style={{ backgroundColor: '#f5f5f7' }}>
          <Canvas
            style={{
              width: `${state.canvasSize.width * (zoom / 100)}px`,
              height: `${state.canvasSize.height * (zoom / 100)}px`,
              maxWidth: 'none',
              maxHeight: 'none',
              background: state.backgroundColor,
              pointerEvents: 'auto'
            }}
            camera={{ position: [0, 0, 5], fov: 75 }}
            frameloop="always"
            dpr={[1, 2]}
            gl={{ 
              antialias: true,
              alpha: false,
              preserveDrawingBuffer: false
            }}
            onCreated={({ gl, scene }) => {
              // Stelle sicher, dass das Canvas richtig initialisiert ist
              gl.setClearColor(state.backgroundColor || '#1a1a1a', 1);
              gl.setPixelRatio(Math.min(window.devicePixelRatio, 2));
            }}
          >
              {/* Camera */}
              <CameraSetup
                cameraFOV={state.camera.fov}
                cameraNear={state.camera.near}
                cameraFar={state.camera.far}
                cameraPositionX={state.camera.position.x}
                cameraPositionY={state.camera.position.y}
                cameraPositionZ={state.camera.position.z}
                cameraRotationX={state.camera.rotation.x}
                cameraRotationY={state.camera.rotation.y}
                cameraRotationZ={state.camera.rotation.z}
              />
              {/* Camera Reset - wird durch Button-Trigger aktiviert */}
              <CameraReset
                targetPosition={[0, 0, 5]}
                targetRotation={[0, 0, 0]}
                resetTrigger={cameraResetTrigger}
              />

              {/* Scene Background - Immer gesetzt */}
              <SceneBackground color={state.backgroundColor} />

              {/* Lighting - Immer aktiv für Live-Vorschau */}
              <AmbientLightComponent
                color={state.lighting.ambient.color}
                intensity={state.lighting.ambient.intensity}
              />
              <DirectionalLightComponent
                randomizer={state.randomizers.lighting}
                isExporting={isExporting}
                color={state.lighting.directional.color}
                intensity={state.lighting.directional.intensity}
                position={state.lighting.directional.position}
                colorRange={state.randomizerSettings.light.colorRange}
                intensityRange={state.randomizerSettings.light.intensityRange}
                positionRange={state.randomizerSettings.light.positionRange}
              />
              <EnvironmentWrapper 
                preset={state.lighting.environment.preset} 
                background 
                blur={state.lighting.environment.blur}
                customHDRUrl={state.lighting.environment.customHDRUrl}
                backgroundColor={state.backgroundColor}
                randomizeEnvironment={state.randomizers.environment && !state.lighting.environment.customHDRUrl}
                isExporting={isExporting}
                currentFrame={currentFrame}
                frameInterval={state.randomizerSettings.environment?.frameInterval || 30}
                availableHDRs={availableHDRs}
              />

              {/* Background Spawner */}
              {state.randomizers.backgroundSpawner && (
                <BackgroundSpawner
                  ref={backgroundSpawnerRef}
                  textureFolder="textures/background"
                  minObjects={state.randomizerSettings.backgroundSpawner.minObjects}
                  maxObjects={state.randomizerSettings.backgroundSpawner.maxObjects}
                  minSize={state.randomizerSettings.backgroundSpawner.minSize}
                  maxSize={state.randomizerSettings.backgroundSpawner.maxSize}
                  positionRange={state.randomizerSettings.backgroundSpawner.positionRange}
                  randomizer={state.randomizers.backgroundSpawner}
                  isExporting={isExporting}
                />
              )}

              {/* Foreground Spawner */}
              {state.randomizers.foregroundSpawner && (
                <ForegroundSpawner
                  ref={foregroundSpawnerRef}
                  textureFolder="textures/foreground"
                  minObjects={state.randomizerSettings.foregroundSpawner.minObjects}
                  maxObjects={state.randomizerSettings.foregroundSpawner.maxObjects}
                  minSize={state.randomizerSettings.foregroundSpawner.minSize}
                  maxSize={state.randomizerSettings.foregroundSpawner.maxSize}
                  positionRange={state.randomizerSettings.foregroundSpawner.positionRange}
                  randomizer={state.randomizers.foregroundSpawner}
                  isExporting={isExporting}
                />
              )}

              {/* Objects - Immer aktiv für Live-Vorschau */}
              {state.objectType === 'model' && state.modelUrl && (
                <ModelMaterial
                  url={state.modelUrl}
                  materialColor={state.material.color}
                  materialMetalness={state.material.metalness}
                  materialRoughness={state.material.roughness}
                  materialClearcoat={state.material.clearcoat}
                  materialClearcoatRoughness={state.material.clearcoatRoughness}
                  materialReflectivity={state.material.reflectivity}
                  materialTransmission={state.material.transmission}
                  materialEmissive={state.material.emissive}
                  materialEmissiveIntensity={state.material.emissiveIntensity}
                  materialTextures={state.materialTextures}
                >
                  {({ model, materials }) => {
                    if (!model) {
                      console.warn('ModelMaterial: Model not ready');
                      return null;
                    }
                    if (!materials) {
                      console.warn('ModelMaterial: Materials not ready, using fallback');
                      // Fallback-Materialien erstellen
                      const fallbackMaterials = [
                        new (require('three').MeshBasicMaterial)({ color: "white", toneMapped: false }),
                        new (require('three').MeshPhysicalMaterial)({
                          color: state.material.color || '#ffffff',
                          metalness: state.material.metalness || 0.3,
                          roughness: state.material.roughness || 0.7,
                          emissive: state.material.emissive || '#ffffff',
                          emissiveIntensity: state.material.emissiveIntensity || 0.2,
                          visible: true
                        })
                      ];
                      return (
                        <ModelSpawner
                          ref={modelSpawnerRef}
                          model={model}
                          materials={fallbackMaterials}
                          groupRef={modelGroupRef}
                          minSpawnPositionX={state.modelSpawn.minPosition.x}
                          maxSpawnPositionX={state.modelSpawn.maxPosition.x}
                          minSpawnPositionY={state.modelSpawn.minPosition.y}
                          maxSpawnPositionY={state.modelSpawn.maxPosition.y}
                          minSpawnPositionZ={state.modelSpawn.minPosition.z}
                          maxSpawnPositionZ={state.modelSpawn.maxPosition.z}
                          minDistanceBetweenObjects={state.modelSpawn.minDistance}
                          minRotation={state.modelSpawn.minRotation}
                          maxRotation={state.modelSpawn.maxRotation}
                          minObjects={state.modelSpawn.minObjects}
                          maxObjects={state.modelSpawn.maxObjects}
                          randomizePosition={state.randomizers.objectPosition && state.randomizers.spawn && isExporting}
                          randomizeRotation={state.randomizers.objectRotation && state.randomizers.spawn && isExporting}
                          isExporting={isExporting && state.randomizers.spawn}
                          scale={state.modelSpawn.scale}
                          modelUrl={state.modelUrl}
                        />
                      );
                    }
                    return (
                      <ModelSpawner
                        ref={modelSpawnerRef}
                        model={model}
                        materials={materials}
                        groupRef={modelGroupRef}
                        minSpawnPositionX={state.modelSpawn.minPosition.x}
                        maxSpawnPositionX={state.modelSpawn.maxPosition.x}
                        minSpawnPositionY={state.modelSpawn.minPosition.y}
                        maxSpawnPositionY={state.modelSpawn.maxPosition.y}
                        minSpawnPositionZ={state.modelSpawn.minPosition.z}
                        maxSpawnPositionZ={state.modelSpawn.maxPosition.z}
                        minDistanceBetweenObjects={state.modelSpawn.minDistance}
                        minRotation={state.modelSpawn.minRotation}
                        maxRotation={state.modelSpawn.maxRotation}
                        minObjects={state.modelSpawn.minObjects}
                        maxObjects={state.modelSpawn.maxObjects}
                        randomizePosition={state.randomizers.objectPosition && state.randomizers.spawn && isExporting}
                        randomizeRotation={state.randomizers.objectRotation && state.randomizers.spawn && isExporting}
                        isExporting={isExporting && state.randomizers.spawn}
                        scale={state.modelSpawn.scale}
                        modelUrl={state.modelUrl}
                      />
                    );
                  }}
                </ModelMaterial>
              )}

              {state.objectType === 'texture' && state.textureUrl && (
                <PlaneSpawner
                  ref={planeSpawnerRef}
                  textureUrl={state.textureUrl}
                  minSpawnPositionX={state.modelSpawn.minPosition.x}
                  maxSpawnPositionX={state.modelSpawn.maxPosition.x}
                  minSpawnPositionY={state.modelSpawn.minPosition.y}
                  maxSpawnPositionY={state.modelSpawn.maxPosition.y}
                  minSpawnPositionZ={state.modelSpawn.minPosition.z}
                  maxSpawnPositionZ={state.modelSpawn.maxPosition.z}
                  minDistanceBetweenObjects={state.modelSpawn.minDistance}
                  minRotation={state.modelSpawn.minRotation}
                  maxRotation={state.modelSpawn.maxRotation}
                  minObjects={state.modelSpawn.minObjects}
                  maxObjects={state.modelSpawn.maxObjects}
                  randomizePosition={state.randomizers.objectPosition && state.randomizers.spawn && isExporting}
                  randomizeRotation={state.randomizers.objectRotation && state.randomizers.spawn && isExporting}
                  isExporting={isExporting && state.randomizers.spawn}
                  scale={state.modelSpawn.scale}
                />
              )}


              {/* Export & Debug */}
              <CanvasContent
                ref={canvasContentRef}
                objectSpawners={objectSpawners}
                imageWidth={state.canvasSize.width}
                imageHeight={state.canvasSize.height}
                exportYOLO={state.exportYOLO}
                annotationType={state.annotationType}
                classes={classManager.classes}
                randomizers={state.randomizers}
                onExportStateChange={setIsExporting}
                onFrameChange={setCurrentFrame}
              />
              
              {!isExporting && (
                <BoundingBoxDebug
                  objectSpawners={objectSpawners}
                  imageWidth={state.canvasSize.width}
                  imageHeight={state.canvasSize.height}
                  showDebug={state.showBoundingBoxes}
                  annotationType={state.annotationType}
                />
              )}

              {/* Helpers */}
        <OrbitControls 
          ref={orbitControlsRef}
          enableDamping={true}
          dampingFactor={0.05}
          rotateSpeed={1}
          zoomSpeed={1}
          panSpeed={1}
        />
        <axesHelper args={[10]} />
        <gridHelper />
          </Canvas>
        </div>

        {/* Right Sidebar - Properties Panel */}
        <div className="w-[340px] min-w-[300px] max-w-[400px] h-[calc(100%-24px)] m-3 overflow-y-auto bg-white rounded-2xl shadow-lg flex flex-col backdrop-blur-sm">
          <div className="flex-1 overflow-y-auto">
            {/* 3D Model Section */}
            <div>
              <div className="px-5 pt-5 pb-3 border-b border-dark-border/60">
                <span className="text-[10px] font-bold text-dark-text-muted uppercase tracking-widest">3D Model</span>
              </div>
              <AccordionPanel title="Upload" icon={<FaFolderOpen />} defaultOpen={true}>
                <Step1Upload state={state} callbacks={callbacks} classManager={classManager} />
              </AccordionPanel>
              <AccordionPanel title="Material" icon={<FaPalette />}>
                <Step1Material state={state} callbacks={callbacks} />
              </AccordionPanel>
            </div>

            {/* Scene Section */}
            <div>
              <div className="px-5 pt-5 pb-3 border-b border-dark-border/60">
                <span className="text-[10px] font-bold text-dark-text-muted uppercase tracking-widest">Szene</span>
              </div>
              <AccordionPanel title="Canvas & Export" icon={<FaImage />}>
                <div className="space-y-4">
                  <Step2Canvas state={state} callbacks={callbacks} />
                  <div className="pt-4 border-t border-dark-border">
                    <Step6Export state={state} callbacks={callbacks} />
                  </div>
                </div>
              </AccordionPanel>
            </div>

            {/* Randomizers Section */}
            <div>
              <div className="px-5 pt-5 pb-3 border-b border-dark-border/60">
                <span className="text-[10px] font-bold text-dark-text-muted uppercase tracking-widest">Randomizer</span>
              </div>
              <AccordionPanel 
                title="Spawn" 
                icon={<FaDice />}
                toggleChecked={state.randomizers.spawn}
                onToggleChange={(checked) => callbacks.setRandomizers({ ...state.randomizers, spawn: checked })}
              >
                <Step3Spawn state={state} callbacks={callbacks} />
              </AccordionPanel>
              <AccordionPanel 
                title="Lighting" 
                icon={<FaLightbulb />}
                toggleChecked={state.randomizers.lighting}
                onToggleChange={(checked) => {
                  callbacks.setRandomizers({ ...state.randomizers, lighting: checked, light: checked });
                }}
              >
                <Step4Lighting state={state} callbacks={callbacks} />
              </AccordionPanel>
              <AccordionPanel 
                title="Environment" 
                icon={<FaImage />}
                toggleChecked={state.randomizers.environment}
                onToggleChange={(checked) => callbacks.setRandomizers({ ...state.randomizers, environment: checked })}
              >
                <RandomizerEnvironment state={state} callbacks={callbacks} />
              </AccordionPanel>
              <AccordionPanel 
                title="Position" 
                icon={<FaMapMarkerAlt />}
                toggleChecked={state.randomizers.objectPosition}
                onToggleChange={(checked) => callbacks.setRandomizers({ ...state.randomizers, objectPosition: checked })}
              >
                <RandomizerObjectPosition state={state} callbacks={callbacks} />
              </AccordionPanel>
              <AccordionPanel 
                title="Rotation" 
                icon={<FaSyncAlt />}
                toggleChecked={state.randomizers.objectRotation}
                onToggleChange={(checked) => callbacks.setRandomizers({ ...state.randomizers, objectRotation: checked })}
              >
                <RandomizerObjectRotation state={state} callbacks={callbacks} />
              </AccordionPanel>
              <AccordionPanel 
                title="Background Spawner" 
                icon={<FaDice />}
                toggleChecked={state.randomizers.backgroundSpawner}
                onToggleChange={(checked) => callbacks.setRandomizers({ ...state.randomizers, backgroundSpawner: checked })}
              >
                <RandomizerBackgroundSpawner state={state} callbacks={callbacks} />
              </AccordionPanel>
              <AccordionPanel 
                title="Foreground Spawner" 
                icon={<FaDice />}
                toggleChecked={state.randomizers.foregroundSpawner}
                onToggleChange={(checked) => callbacks.setRandomizers({ ...state.randomizers, foregroundSpawner: checked })}
              >
                <RandomizerForegroundSpawner state={state} callbacks={callbacks} />
              </AccordionPanel>
            </div>
          </div>
          
          {/* Export Button - Outside Accordion */}
          <div className="p-5 border-t border-dark-border/60 bg-gradient-to-t from-white to-dark-hover/30">
            <button
              className="w-full py-3.5 px-4 bg-dark-text hover:bg-dark-text-secondary border-none rounded-xl text-sm font-semibold text-white cursor-pointer transition-all duration-200 flex items-center justify-center gap-2.5 focus:outline-none focus:ring-2 focus:ring-dark-text focus:ring-offset-2 shadow-medium hover:shadow-lg hover:scale-[1.02] active:scale-[0.98]"
              onClick={callbacks.startExport}
            >
              <FaRocket className="text-sm" /> Daten generieren
            </button>
            <button
              className="mt-3 w-full text-xs text-dark-text-muted hover:text-dark-text cursor-pointer transition-all duration-200 underline hover:no-underline focus:outline-none text-center font-medium"
              onClick={() => {
                if (window.confirm('Möchten Sie wirklich alle Einstellungen zurücksetzen? Dies kann nicht rückgängig gemacht werden.')) {
                  // Lösche alle LocalStorage-Einträge mit 'app_' Präfix
                  Object.keys(localStorage).forEach(key => {
                    if (key.startsWith('app_')) {
                      localStorage.removeItem(key);
                    }
                  });
                  // Lade die Seite neu, um Standardwerte zu laden
                  window.location.reload();
                }
              }}
              title="Alle Einstellungen zurücksetzen"
            >
              Einstellungen zurücksetzen
            </button>
          </div>
        </div>
      </div>

      {/* File Inputs */}
      <input type="file" ref={modelFileInputRef} onChange={handleModelUpload} className="hidden" />
      <input type="file" ref={textureFileInputRef} onChange={handleTextureUpload} className="hidden" />

      <Loader />
    </main>
  );
};

export default App;
